import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ProjectId } from 'api/models';
import { useNativeMembers } from 'data/members/hooks/use-store-members';
import { useProjectUsers } from 'hooks/use-project-users';
import { isValidEmail as checkEmailRegex } from 'utils/is-valid-email';

const ErrorMessage = styled.div`
  color: var(--red-60);
`;

export const useExternalInviteUserActions = ({
  projectId,
  allOptions,
  inValidOptions,
}) => {
  const { members: nativeMembers } = useNativeMembers();
  const {
    content: [, projectMembers = []],
  } = useProjectUsers(ProjectId(projectId));

  const invalidOptions = useMemo(
    () => allOptions.filter((option) => inValidOptions.includes(option.userId)),
    [allOptions, inValidOptions],
  );

  const isNativeMember = useCallback(
    (emailId: string) =>
      nativeMembers.some((members) => members.user.emailId === emailId),
    [nativeMembers],
  );

  const isProjectMember = useCallback(
    (emailId: string) =>
      projectMembers.some((members) => members.emailId === emailId),
    [projectMembers],
  );

  const isDuplicateInput = useCallback(
    (emailId: string) =>
      invalidOptions?.find((user) => user.emailId === emailId),
    [invalidOptions],
  );

  const isEmailValid = useCallback(
    (emailId: string) =>
      checkEmailRegex(emailId) &&
      !isDuplicateInput(emailId) &&
      !isProjectMember(emailId) &&
      !isNativeMember(emailId),
    [isNativeMember, isProjectMember, isDuplicateInput],
  );

  const getNoOptionsMessage = ({ inputValue }) => {
    if (isProjectMember(inputValue)) {
      return (
        <ErrorMessage>
          <FormattedMessage
            id="existing.invitation"
            values={{
              user: inputValue,
            }}
          />
        </ErrorMessage>
      );
    }

    if (isNativeMember(inputValue)) {
      return <FormattedMessage id="request.invitation" />;
    }

    return <FormattedMessage id="validations.invite_email" />;
  };

  return {
    isProjectMember,
    isNativeMember,
    isEmailValid,
    getNoOptionsMessage,
  };
};
